import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import type { Style } from '../component/elements/StyleJigsaw';
import { newStyles } from '../component/elements/StyleJigsaw';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import {
  AUDIO_GENERATION_LIMIT,
  LANGUAGE_DEFAULT_TEXTS,
  NEW_EDITOR_DEFAULT_TEXT,
  NEW_EDITOR_DEFAULT_VOICE,
  NEW_EDITOR_STYLE_TEXTS,
} from '../lib/constants';
import type { translations } from '../lib/translations';
import type { Prosody, VoiceDemoDataType } from '../lib/types';
import { PLAY_STATE } from '../lib/types';
import { getVoicePreferenceFromStyle } from '../lib/utils';

import { useVoices } from './VoicesProvider';
import { logEvent } from '../lib/analytics';

interface Error {
  message: string;
  type: 'rateLimit' | 'general';
}
interface VoiceDemoContextProps {
  currentVoice: VoiceDemoDataType['voices'][0];
  currentLanguage: VoiceDemoDataType;
  currentContent: string;
  currentStyle: string | null;
  prosody: Prosody;
  isPlayerLoading: boolean;
  playerState: PLAY_STATE;
  showReplay: boolean;
  audioRef: React.MutableRefObject<HTMLAudioElement | null | undefined>;
  error: Error | null;
  showUpsell: boolean;
  handleUpdateLanguage: (languageCode: string) => void;
  handleUpdateStyle: (style: Style) => void;
  handleUpdateVoice: (voiceName: string) => void;
  handleUpdateTextContent: (content: string) => void;
  handleUpdateProsody: (updatedProsody: Prosody) => void;
  togglePlayback: () => void;
  stopAndReset: () => void;
  setShowUpsell: (value: boolean) => void;
  audioDataUrl?: string;
  audioDownloadUrl?: string;
  numGeneration: number;
  requestIdRef: React.MutableRefObject<number>;
}

const VoiceDemoContext = createContext<VoiceDemoContextProps | undefined>(
  undefined,
);

export const VoiceDemoProvider: React.FC<{
  initialLanguage?: string;
  children: React.ReactNode;
}> = ({ initialLanguage, children }) => {
  const { voices: allVoicesData, styleText } = useVoices();

  const initialVoiceDataFromParams = initialLanguage
    ? allVoicesData.find(
        (language) =>
          language.languageName.toLowerCase() === initialLanguage.toLowerCase(),
      )
    : null;

  const defaultVoiceData = initialVoiceDataFromParams ?? allVoicesData[0];
  const currentLanguageCode = defaultVoiceData.languageCode.split(
    '-',
  )[0] as keyof typeof translations;

  const getInitialDemoText = () => {
    return (
      LANGUAGE_DEFAULT_TEXTS[defaultVoiceData.languageCode] ||
      NEW_EDITOR_DEFAULT_TEXT
    );
  };

  const initialDemoText = getInitialDemoText();

  const getInitialVoice = () => {
    // Try to find the preferred voice, fall back to female voice, then any voice
    const defaultVoice =
      defaultVoiceData.voices.find(
        (voice) => voice.displayName === NEW_EDITOR_DEFAULT_VOICE,
      ) ??
      defaultVoiceData.voices.find((voice) => voice.gender === 'female') ??
      defaultVoiceData.voices[0];
    return defaultVoice;
  };

  const [currentVoice, setCurrentVoice] = useState(getInitialVoice());
  const [currentLanguage, setCurrentLanguage] = useState(defaultVoiceData);
  const [currentContent, setCurrentContent] = useState(initialDemoText);
  const [currentStyle, setCurrentStyle] = useState<Style | null>(
    'Story Telling',
  );
  const [isContentEdited, setIsContentEdited] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [prosody, setProsody] = useState({ rate: 1 });
  const {
    stopAndReset,
    togglePlayback,
    playerState,
    showReplay,
    audioRef,
    error,
    setIsContentChanged,
    showUpsell,
    setShowUpsell,
    audioDataUrl,
    audioDownloadUrl,
    numGeneration,
    requestIdRef,
  } = useAudioPlayer(currentContent, currentStyle, prosody, currentVoice);

  const isPlayerLoading = playerState === PLAY_STATE.LOADING || !currentContent;

  useEffect(() => {
    if (!isInitialized && !isContentEdited) {
      const translatedText =
        LANGUAGE_DEFAULT_TEXTS[currentLanguage.languageCode] ||
        NEW_EDITOR_DEFAULT_TEXT;
      setCurrentContent(translatedText);

      const defaultVoice = currentLanguage.voices.find(
        (voice) => voice.displayName === NEW_EDITOR_DEFAULT_VOICE,
      );
      if (defaultVoice) {
        setCurrentVoice(defaultVoice);
      }
      setIsInitialized(true);
    }
  }, [
    currentLanguage.languageCode,
    currentLanguage.voices,
    isContentEdited,
    isInitialized,
  ]);

  useEffect(() => {
    if (numGeneration >= AUDIO_GENERATION_LIMIT) {
      logEvent('player_interaction', {
        action: 'audio_generation_limit_reached',
        state: playerState,
      });
    }
  },[numGeneration, playerState])

  const handleUpdateLanguage = (languageCode: string) => {
    const newLanguage = allVoicesData.find(
      (language) => language.languageCode === languageCode,
    );
    stopAndReset();

    if (newLanguage) {
      setCurrentLanguage(newLanguage);
      setCurrentStyle('Story Telling');

      // Try to find the preferred voice, fall back to female voice, then any voice
      const defaultVoice =
        newLanguage.voices.find(
          (voice) => voice.displayName === NEW_EDITOR_DEFAULT_VOICE,
        ) ??
        newLanguage.voices.find((voice) => voice.gender === 'female') ??
        newLanguage.voices[0];
      setCurrentVoice(defaultVoice);

      const translatedText =
        LANGUAGE_DEFAULT_TEXTS[languageCode] || NEW_EDITOR_DEFAULT_TEXT;
      setCurrentContent(translatedText);
      setIsContentEdited(false);
    }
  };

  const getStyleText = useCallback(
    (locale: string, style: string) => {
      let text = '';
      try {
        text = styleText[locale][style];
      } catch (err) {
        console.error(err);
      }
      if (!text || text.length === 0)
        console.error(
          new Error(`empty style text found for ${locale} ${style}`),
        );
      return text ?? '';
    },
    [styleText],
  );

  const handleUpdateStyle = (style: Style) => {
    if (style === currentStyle) {
      setCurrentStyle(null);
    } else {
      setCurrentStyle(style);

      const styleDetail = newStyles.find((s) => s.style === style);

      if (styleDetail?.defaultText) {
        // For new editor, first try constants-based localized text
        const locale = currentLanguage.languageCode;
        const localizedNewEditor =
          NEW_EDITOR_STYLE_TEXTS[locale]?.[style] ?? '';
        const localizedOld = getStyleText(currentVoice.languageCode, style);
        const nextText =
          localizedNewEditor || localizedOld || styleDetail.defaultText;
        setCurrentContent(nextText);

        if (styleDetail.defaultVoice) {
          const newVoice = currentLanguage.voices.find(
            (voice) => voice.displayName === styleDetail.defaultVoice,
          );
          if (newVoice) {
            setCurrentVoice(newVoice);
          }
        }
      } else {
        // Fall back to old styleText approach for backward compatibility
        setCurrentContent(getStyleText(currentVoice.languageCode, style));
        const voice = getVoicePreferenceFromStyle(
          currentLanguage.voices,
          style,
        );
        if (voice) setCurrentVoice(voice);
      }
    }
    setIsContentEdited(true);
    stopAndReset();
    setIsContentChanged(true);
  };

  const handleUpdateVoice = (voiceName: string) => {
    const newVoice = currentLanguage.voices.find(
      (voice) => voice.name === voiceName,
    );
    stopAndReset();

    if (newVoice) {
      setCurrentVoice(newVoice);
      setIsContentChanged(true);
    }
  };

  const handleUpdateTextContent = (content: string) => {
    setCurrentContent((current) => {
      if (current !== content) {
        setIsContentEdited(true);
        stopAndReset();
        setIsContentChanged(true);
      }
      return content;
    });
  };

  const handleUpdateProsody = (updatedProsody: Prosody) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = updatedProsody.rate;
    }
    setProsody(updatedProsody);
  };

  return (
    <VoiceDemoContext.Provider
      value={{
        currentVoice,
        currentLanguage,
        currentContent,
        currentStyle,
        prosody,
        isPlayerLoading,
        playerState,
        showReplay,
        audioRef,
        error,
        showUpsell,
        handleUpdateLanguage,
        handleUpdateStyle,
        handleUpdateVoice,
        handleUpdateTextContent,
        handleUpdateProsody,
        togglePlayback,
        stopAndReset,
        setShowUpsell,
        audioDataUrl,
        audioDownloadUrl,
        numGeneration,
        requestIdRef,
      }}>
      {children}
    </VoiceDemoContext.Provider>
  );
};

export const useVoiceDemo = () => {
  const context = useContext(VoiceDemoContext);
  if (!context) {
    throw new Error('useVoiceDemo must be used within a VoiceDemoProvider');
  }
  return context;
};
